import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  Check,
  ChevronLeft,
  ChevronRight,
  Filter,
  Pencil,
  Plus,
  Search,
  Trash2,
  Upload,
} from 'lucide-react';
import type { ReactElement } from 'react';
import { cloneElement, isValidElement, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as XLSX from 'xlsx';

import { useBreadcrumb } from '@/components/common/BreadcrumbContext';
import {
  ColumnPicker,
  tableMinWidthClass,
  type ColumnOption,
} from '@/components/common/ColumnPicker';
import { RhfStateCitySelect } from '@/components/common/RhfStateCitySelect';
import { DateRangeFilterDialog, type DateRange } from '@/components/sales/DateRangeFilterDialog';
import { FilterGroup } from '@/components/sales/FilterGroup';
import { SalesTableState } from '@/components/sales/SalesTableState';
import { SortDialog } from '@/components/sales/SortDialog';
import { StatusChip, type ChipTone } from '@/components/sales/StatusChip';
import LeadWizard from '@/components/sales/wizard/LeadWizard';
import { SelectMenu, useToast } from '@/components/ui';
// The NATIVE, react-hook-form-friendly select (see the note in select-menu.tsx):
// `register()` needs a real <select> it can attach a ref and an onChange to,
// while the Radix `Select` imported below takes a value/onChange pair instead.
// Aliased so both can coexist here - the Radix one drives rows-per-page.
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select } from '@/components/ui/select-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  addLeadSchema,
  LEAD_SOURCE_OPTIONS,
  type AddLeadFormValues,
} from '@/features/sales-rm/addLeadSchema';
import useClampPage from '@/hooks/useClampPage';
import useDebounce from '@/hooks/useDebounce';
import {
  useCreateLead,
  useDeleteLead,
  useImportLeads,
  useInvalidateLeads,
  useLeads,
  useUpdateLead,
} from '@/hooks/useLeads';
import usePersistedState from '@/hooks/usePersistedState';
import { useTableColumns } from '@/hooks/useTableColumns';
import {
  formatValidTill,
  kydLabel,
  kydTone,
  leadFullName,
  type LeadRecord,
} from '@/lib/sales-metrics';
import {
  leadsService,
  type CreateLeadPayload,
  type LeadsListParams,
  type UpdateLeadPayload,
} from '@/services/leads.service';
import { errorMessage } from '@/utils/apiError';
import { normalizeArn } from '@/utils/arn';
import { normalizeStateName } from '@/utils/indiaStates';

type SortKey =
  | 'arn'
  | 'firstName'
  | 'lastName'
  | 'brandName'
  | 'city'
  | 'pincode'
  | 'kydCompliant'
  | 'arnValidTill'
  | 'tcStatus'
  | 'leadStatus'
  | 'paymentStatus';

const SORT_OPTIONS: { key: SortKey; label: string }[] = [
  { key: 'arn', label: 'ARN' },
  { key: 'firstName', label: 'First Name' },
  { key: 'lastName', label: 'Last Name' },
  { key: 'brandName', label: 'Brand Name' },
  { key: 'city', label: 'City' },
  { key: 'pincode', label: 'Pincode' },
  { key: 'kydCompliant', label: 'KYD Compliant' },
  { key: 'arnValidTill', label: 'ARN Valid Date' },
  { key: 'tcStatus', label: 'Status of T&C' },
  { key: 'leadStatus', label: 'Lead Status' },
  { key: 'paymentStatus', label: 'Payment Status' },
];

type LeadColumnKey =
  | 'leadCode'
  | 'arn'
  | 'name'
  | 'brandName'
  | 'email'
  | 'mobile'
  | 'city'
  | 'pincode'
  | 'kydCompliant'
  | 'arnValidTill'
  | 'tcStatus'
  | 'leadStatus'
  | 'paymentStatus';

// Order here is the order of the table AND of the picker's checkbox list. The
// Actions column is absent because it is not a column of data.
const LEAD_COLUMNS: readonly ColumnOption<LeadColumnKey>[] = [
  { key: 'leadCode', label: 'ID' },
  { key: 'arn', label: 'ARN' },
  { key: 'name', label: 'Name' },
  { key: 'brandName', label: 'Brand Name' },
  { key: 'email', label: 'Email' },
  { key: 'mobile', label: 'Mobile' },
  { key: 'city', label: 'City' },
  { key: 'pincode', label: 'Pincode' },
  { key: 'kydCompliant', label: 'KYD Compliant' },
  { key: 'arnValidTill', label: 'ARN Valid Date' },
  { key: 'tcStatus', label: 'T&C Status' },
  { key: 'leadStatus', label: 'Lead Status' },
  { key: 'paymentStatus', label: 'Payment Status' },
];

const LEAD_COLUMN_KEYS = LEAD_COLUMNS.map((column) => column.key);

// The six that identify WHICH lead a row is. Hiding any of them leaves rows an
// RM cannot tell apart, so they are pinned on rather than offered as a choice.
const LEAD_LOCKED_COLUMNS: readonly LeadColumnKey[] = [
  'leadCode',
  'arn',
  'name',
  'brandName',
  'email',
  'mobile',
];

const TC_OPTIONS = ['Not sent', 'Not Confirmed', 'Confirmed'];
const LEAD_OPTIONS = ['Lead', 'Future Client'];
const PAY_OPTIONS = ['Not Paid', 'Partial Payment', 'Full Payment'];
const ALL_ROWS = 1000;
const ROWS_PER_PAGE_OPTIONS: { value: number; label: string }[] = [
  { value: 10, label: '10' },
  { value: 25, label: '25' },
  { value: 50, label: '50' },
  { value: 100, label: '100' },
  { value: ALL_ROWS, label: 'All' },
];

const tcTone = (s: string): ChipTone =>
  s === 'Confirmed' ? 'success' : s === 'Not Confirmed' ? 'warning' : 'neutral';
const leadTone = (s: string): ChipTone =>
  s === 'Client' ? 'success' : s === 'Future Client' ? 'info' : 'neutral';
const payTone = (s: string): ChipTone =>
  s === 'Full Payment' ? 'success' : s === 'Partial Payment' ? 'warning' : 'neutral';

/** True when the stored channel is one the select currently offers. */
const isOfferedLeadSource = (stored: string): boolean =>
  LEAD_SOURCE_OPTIONS.some((option) => option === stored);

/**
 * The lead-source half of an edit PATCH - present ONLY for a field the RM
 * actually changed.
 *
 * This is the guard that stops an unrelated edit from destroying attribution.
 * Fixing a typo in the email leaves both controls holding exactly what the lead
 * already stores, so neither key is emitted, the JSON body never mentions them,
 * and the server (which distinguishes `undefined` from `null`) leaves
 * lead_source, lead_source_detail AND lead_source_captured_at untouched.
 *
 * Clearing still clears: moving the select off a recorded channel onto the empty
 * option is a change, so `leadSource: null` IS sent and the column IS cleared.
 * The difference is that it now requires a visible, deliberate act rather than
 * happening as a side effect of saving a dialog the RM never touched.
 *
 * Compared against the stored record rather than react-hook-form's dirtyFields:
 * the question that matters is "does this differ from what is on disk", which
 * survives a value being changed and changed back, and needs no assumptions
 * about how the form library tracks dirtiness.
 */
const leadSourcePatch = (
  stored: LeadRecord,
  values: AddLeadFormValues,
): Pick<UpdateLeadPayload, 'leadSource' | 'leadSourceDetail'> => {
  const patch: Pick<UpdateLeadPayload, 'leadSource' | 'leadSourceDetail'> = {};
  const nextSource = values.leadSource ?? '';
  if (nextSource !== (stored.leadSource ?? '')) {
    patch.leadSource = nextSource === '' ? null : nextSource;
  }
  const nextDetail = values.leadSourceDetail ?? '';
  if (nextDetail !== (stored.leadSourceDetail ?? '')) {
    patch.leadSourceDetail = nextDetail === '' ? null : nextDetail;
  }
  return patch;
};

const pick = (row: Record<string, unknown>, keys: string[]): string => {
  const lower: Record<string, unknown> = {};
  Object.keys(row).forEach((k) => {
    lower[k.toLowerCase().trim()] = row[k];
  });
  const found = keys
    .map((k) => k.toLowerCase())
    .find((k) => lower[k] !== undefined && lower[k] !== '');
  return found ? String(lower[found]).trim() : '';
};

const SalesLeadsPage = () => {
  const { toast } = useToast();
  // Filters/search/sort/pagination persist across refresh and navigation
  // (sessionStorage, see usePersistedState). Dialog/selection state stays local.
  const [searchInput, setSearchInput] = usePersistedState('salesLeads:search', '');
  const search = useDebounce(searchInput, 350);
  const [page, setPage] = usePersistedState('salesLeads:page', 1);
  const [limit, setLimit] = usePersistedState('salesLeads:limit', 25);
  const [sortBy, setSortBy] = usePersistedState<SortKey | undefined>(
    'salesLeads:sortBy',
    undefined,
  );
  const [sortDir, setSortDir] = usePersistedState<'asc' | 'desc'>('salesLeads:sortDir', 'asc');
  const [tcFilter, setTcFilter] = usePersistedState<string[]>('salesLeads:tcFilter', []);
  const [leadFilter, setLeadFilter] = usePersistedState<string[]>('salesLeads:leadFilter', []);
  const [payFilter, setPayFilter] = usePersistedState<string[]>('salesLeads:payFilter', []);
  const [dateRange, setDateRange] = usePersistedState<DateRange | null>(
    'salesLeads:dateRange',
    null,
  );
  // Which columns this table shows, persisted alongside the filters above.
  const columns = useTableColumns<LeadColumnKey>(
    'salesLeads:columns',
    LEAD_COLUMN_KEYS,
    LEAD_LOCKED_COLUMNS,
  );
  const [addOpen, setAddOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState<LeadRecord | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<LeadRecord | null>(null);
  const [editTarget, setEditTarget] = useState<LeadRecord | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const { setExtra } = useBreadcrumb();
  useEffect(() => {
    setExtra(selectedLead ? leadFullName(selectedLead) : null);
    return () => setExtra(null);
  }, [selectedLead, setExtra]);

  const activeFilters = tcFilter.length + leadFilter.length + payFilter.length;

  // Never use page < 1: it fails the server's page.min(1) validator and makes
  // the "Showing X to Y" range go negative. Guards the single render before
  // useClampPage repairs a stale page=0 from sessionStorage.
  const safePage = Math.max(1, page);

  const params = useMemo<LeadsListParams>(
    () => ({
      page: safePage,
      limit,
      search: search || undefined,
      sortBy,
      sortDir,
      from: dateRange?.from,
      to: dateRange?.to,
      tcStatus: tcFilter.length ? tcFilter : undefined,
      leadStatus: leadFilter.length ? leadFilter : undefined,
      paymentStatus: payFilter.length ? payFilter : undefined,
    }),
    [safePage, limit, search, sortBy, sortDir, dateRange, tcFilter, leadFilter, payFilter],
  );

  const { data, isLoading, isError, refetch } = useLeads(params);
  const createLead = useCreateLead();
  const importLeads = useImportLeads();
  const updateLead = useUpdateLead();
  const deleteLead = useDeleteLead();
  const invalidateLeads = useInvalidateLeads();

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;
  // A persisted page can outrun the list after it shrinks; pull it back in range.
  useClampPage(page, totalPages, !isLoading, setPage);
  const fromRow = total === 0 ? 0 : (safePage - 1) * limit + 1;
  const toRow = Math.min(total, safePage * limit);

  const toggleSort = (key: SortKey) => {
    if (sortBy === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDir('asc');
    }
    setPage(1);
  };

  const clearSort = () => {
    setSortBy(undefined);
    setSortDir('asc');
    setPage(1);
  };

  const {
    register,
    handleSubmit,
    reset,
    control,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<AddLeadFormValues>({
    resolver: zodResolver(addLeadSchema),
    defaultValues: { isNonArn: false },
  });

  // Client type (ADKJ-117): a NON-ARN client has no ARN to type or check - the
  // server mints their 'NA…' code on save.
  const addIsNonArn = watch('isNonArn') ?? false;

  // Debounced, indexed duplicate-ARN check for the Add Lead form - warns (does
  // not block) when the typed ARN already exists for any RM.
  const watchedArn = watch('arn');
  const normalizedAddArn = (watchedArn && normalizeArn(watchedArn)) || '';
  const debouncedAddArn = useDebounce(normalizedAddArn, 400);
  const { data: arnCheck } = useQuery({
    queryKey: ['arn-check', debouncedAddArn],
    queryFn: () => leadsService.checkArn(debouncedAddArn),
    enabled: addOpen && !addIsNonArn && debouncedAddArn.length === 6,
    staleTime: 30_000,
  });
  let arnWarning: string | undefined;
  if (arnCheck?.exists) {
    arnWarning = arnCheck.ownedByMe
      ? 'You already have a lead with this ARN.'
      : 'This ARN already exists in the system (registered by another RM).';
  }

  const {
    register: registerEdit,
    handleSubmit: handleEditSubmit,
    reset: resetEdit,
    control: controlEdit,
    setValue: setValueEdit,
    watch: watchEdit,
    formState: { errors: editErrors, isSubmitting: isEditSubmitting },
  } = useForm<AddLeadFormValues>({ resolver: zodResolver(addLeadSchema) });

  const editIsNonArn = watchEdit('isNonArn') ?? false;

  useEffect(() => {
    if (!editTarget) return;
    resetEdit({
      isNonArn: editTarget.isNonArn,
      // A non-ARN lead's stored code is not an editable ARN; leave the field
      // empty so switching them to ARN holder starts from a blank input.
      arn: editTarget.isNonArn ? '' : editTarget.arn,
      firstName: editTarget.firstName,
      middleName: editTarget.middleName ?? '',
      lastName: editTarget.lastName ?? '',
      mobile: editTarget.mobile,
      email: editTarget.email,
      city: editTarget.city ?? '',
      state: editTarget.state ?? '',
      pincode: editTarget.pincode ?? '',
      brandName: editTarget.brandName ?? '',
      // Loaded VERBATIM, including a value the current option list no longer
      // offers - the dialog below renders it as a real option so the form can
      // hand it back unchanged. '' is the "not recorded" state, so a lead that
      // predates capture opens with the select empty rather than silently
      // defaulting to a channel nobody chose.
      leadSource: editTarget.leadSource ?? '',
      leadSourceDetail: editTarget.leadSourceDetail ?? '',
    });
  }, [editTarget, resetEdit]);

  // The channel as stored on the lead being edited, and whether it is one the
  // select offers. An unrecognised value is shown (and flagged) rather than
  // hidden: the RM cannot make a deliberate choice about a value the dialog
  // pretends is absent.
  const editSourceStored = editTarget?.leadSource ?? '';
  const editSourceIsUnlisted = editSourceStored !== '' && !isOfferedLeadSource(editSourceStored);

  const onEdit = handleEditSubmit(async (values) => {
    if (!editTarget) return;
    const payload: UpdateLeadPayload = {
      isNonArn: values.isNonArn,
      // For a non-ARN client the code is server-owned; never send one back.
      arn: values.isNonArn ? undefined : (normalizeArn(values.arn ?? '') ?? undefined),
      firstName: values.firstName,
      middleName: values.middleName || undefined,
      lastName: values.lastName,
      mobile: values.mobile,
      email: values.email,
      city: values.city || undefined,
      state: values.state || undefined,
      pincode: values.pincode || undefined,
      // '' is a deliberate clear, not an omission: unlike lead source there is
      // nothing to protect here (a brand name carries no capture instant and no
      // attribution), so an emptied field sends null and clears the column.
      brandName: values.brandName?.trim() ? values.brandName.trim() : null,
      // Only the lead-source fields the RM actually changed (see
      // leadSourcePatch). An untouched control emits NO key at all, so an edit
      // made for some other reason cannot delete the channel, the campaign name
      // or the capture instant; an actual clear still sends `null` and still
      // clears the column.
      ...leadSourcePatch(editTarget, values),
    };
    try {
      await updateLead.mutateAsync({ id: editTarget.id, payload });
      toast.success('Lead updated');
      setEditTarget(null);
    } catch {
      toast.error('Could not update lead');
    }
  });

  const onAdd = handleSubmit(async (values) => {
    const payload: CreateLeadPayload = {
      isNonArn: values.isNonArn,
      arn: values.isNonArn ? undefined : (normalizeArn(values.arn ?? '') ?? undefined),
      firstName: values.firstName,
      middleName: values.middleName || undefined,
      lastName: values.lastName,
      mobile: values.mobile,
      email: values.email,
      city: values.city || undefined,
      state: values.state || undefined,
      pincode: values.pincode || undefined,
      brandName: values.brandName || undefined,
      // Omitted entirely when blank. The server treats an absent source as
      // "never recorded" and leaves all three columns NULL, which is the honest
      // state - it must never invent a channel on the RM's behalf.
      leadSource: values.leadSource || undefined,
      leadSourceDetail: values.leadSourceDetail || undefined,
    };
    try {
      await createLead.mutateAsync(payload);
      toast.success('Lead added');
      reset();
      setAddOpen(false);
    } catch (err) {
      // Surface the server's reason - notably the 409 "A lead with ARN … already
      // exists" from the unique-ARN guard - instead of a generic line.
      toast.error(errorMessage(err, 'Could not add lead'));
    }
  });

  const onImportFile = async (file: File) => {
    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: '' });
      const leads: CreateLeadPayload[] = [];
      let skipped = 0;
      json.forEach((row) => {
        const rawArn = pick(row, ['arn']);
        const arn = rawArn ? normalizeArn(rawArn) : null;
        // Support both split columns and a combined "name" / "full name" column.
        const combinedName = pick(row, ['fullName', 'full name', 'name']);
        const combinedParts = combinedName.split(/\s+/).filter(Boolean);
        const firstName =
          pick(row, ['firstName', 'first name', 'first_name']) || combinedParts[0] || '';
        const lastName =
          pick(row, ['lastName', 'last name', 'last_name']) || combinedParts.slice(1).join(' ');
        const middleName = pick(row, ['middleName', 'middle name', 'middle_name']) || undefined;
        const mobile = pick(row, ['mobile', 'phone', 'mobile number']);
        const email = pick(row, ['email', 'email address']);
        // Wholly empty row (spreadsheet padding): ignore without counting.
        if (!arn && !firstName && !lastName && !mobile && !email) return;
        if (!arn || !firstName || !/^[6-9]\d{9}$/.test(mobile) || !email) {
          skipped += 1;
          return;
        }
        leads.push({
          arn,
          firstName,
          middleName,
          lastName: lastName || undefined,
          mobile,
          email,
          city: pick(row, ['city']) || undefined,
          state: normalizeStateName(pick(row, ['state'])) || undefined,
          pincode: pick(row, ['pincode', 'pin', 'zip']) || undefined,
        });
      });
      if (!leads.length) {
        toast.error(
          'No valid rows found. Expect columns: ARN, First Name, Last Name, Mobile, Email.',
        );
        return;
      }
      const result = await importLeads.mutateAsync(leads);
      // Two kinds of skips: malformed rows dropped while parsing the file, and
      // rows the server skipped because their ARN already exists (duplicate).
      const notes: string[] = [];
      if (skipped) notes.push(`${skipped} invalid (missing ARN, name, mobile or email)`);
      if (result.skipped) notes.push(`${result.skipped} duplicate ARN`);
      const skipNote = notes.length ? ` (${notes.join(', ')} skipped)` : '';
      toast.success(`${result.created} lead(s) imported${skipNote}`);
    } catch {
      toast.error('Could not read the file. Use a valid CSV or XLSX.');
    }
  };

  // Clicking a lead opens the full-page onboarding wizard (in-page view swap).
  if (selectedLead) {
    return (
      <LeadWizard
        lead={selectedLead}
        onBack={() => {
          invalidateLeads();
          setSelectedLead(null);
        }}
        onOnboarded={() => setSelectedLead(null)}
      />
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-[1800px] flex-col gap-3 lg:h-full lg:min-h-0 lg:overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="relative w-full max-w-md">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            aria-label="Search leads"
            placeholder="Search leads…"
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              setPage(1);
            }}
            className="pl-9"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <SortDialog
            options={SORT_OPTIONS}
            sortBy={sortBy}
            sortDir={sortDir}
            onChange={toggleSort}
            onClear={clearSort}
          />

          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1.5">
                <Filter className="h-4 w-4" />
                Filters
                {activeFilters > 0 && (
                  <span className="ml-1 rounded-full bg-primary px-1.5 text-[10px] font-bold text-primary-foreground">
                    {activeFilters}
                  </span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-64 space-y-3">
              <FilterGroup
                label="T&C Status"
                options={TC_OPTIONS}
                selected={tcFilter}
                onChange={(v) => {
                  setTcFilter(v);
                  setPage(1);
                }}
              />
              <FilterGroup
                label="Lead Status"
                options={LEAD_OPTIONS}
                selected={leadFilter}
                onChange={(v) => {
                  setLeadFilter(v);
                  setPage(1);
                }}
              />
              <FilterGroup
                label="Payment Status"
                options={PAY_OPTIONS}
                selected={payFilter}
                onChange={(v) => {
                  setPayFilter(v);
                  setPage(1);
                }}
              />
              {activeFilters > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full"
                  onClick={() => {
                    setTcFilter([]);
                    setLeadFilter([]);
                    setPayFilter([]);
                    setPage(1);
                  }}
                >
                  Clear all
                </Button>
              )}
            </PopoverContent>
          </Popover>

          <DateRangeFilterDialog
            value={dateRange}
            onApply={(r) => {
              setDateRange(r);
              setPage(1);
            }}
          />

          <input
            ref={fileRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void onImportFile(file);
              e.target.value = '';
            }}
          />
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={() => fileRef.current?.click()}
            disabled={importLeads.isPending}
          >
            <Upload className="h-4 w-4" />
            Import (CSV / XLSX)
          </Button>

          <Button size="sm" className="gap-1.5" onClick={() => setAddOpen(true)}>
            <Plus className="h-4 w-4" />
            Add Lead
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>Rows per page</span>
          <div className="w-20">
            <Select
              aria-label="Rows per page"
              value={String(limit)}
              onChange={(e) => {
                setLimit(Number(e.target.value));
                setPage(1);
              }}
            >
              {ROWS_PER_PAGE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <ColumnPicker columns={LEAD_COLUMNS} api={columns} tableLabel="leads table" />
          <span className="text-sm text-muted-foreground">
            {fromRow}–{toRow} of {total}
          </span>
          <Button
            variant="outline"
            size="icon"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            aria-label="Previous page"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            aria-label="Next page"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="flex flex-col rounded-xl border border-border bg-card lg:min-h-0 lg:flex-1 lg:overflow-hidden">
        {isLoading ? (
          <div className="flex h-full min-h-[320px] items-center justify-center py-14 text-sm text-muted-foreground">
            Loading leads…
          </div>
        ) : isError ? (
          <SalesTableState
            variant="error"
            title="Couldn't load leads"
            description="Something went wrong while fetching your leads. Please try again."
            onRetry={() => {
              void refetch();
            }}
            className="h-full min-h-[320px]"
          />
        ) : rows.length === 0 ? (
          <SalesTableState
            variant="empty"
            title="No leads yet"
            description="Add a lead manually or import a CSV / XLSX to get started."
            className="h-full min-h-[320px]"
            action={
              <>
                <Button size="sm" className="gap-1.5" onClick={() => setAddOpen(true)}>
                  <Plus className="h-4 w-4" />
                  Add Lead
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5"
                  onClick={() => fileRef.current?.click()}
                  disabled={importLeads.isPending}
                >
                  <Upload className="h-4 w-4" />
                  Import (CSV / XLSX)
                </Button>
              </>
            }
          />
        ) : (
          <div className="lg:min-h-0 lg:flex-1 lg:overflow-hidden">
            <Table
              containerClassName="max-h-[70dvh] lg:max-h-full"
              className={tableMinWidthClass(columns.hiddenCount)}
            >
              <TableHeader>
                <TableRow>
                  {columns.isVisible('leadCode') && <TableHead>ID</TableHead>}
                  {columns.isVisible('arn') && <TableHead>ARN</TableHead>}
                  {columns.isVisible('name') && <TableHead>Name</TableHead>}
                  {columns.isVisible('brandName') && <TableHead>Brand Name</TableHead>}
                  {columns.isVisible('email') && <TableHead>Email</TableHead>}
                  {columns.isVisible('mobile') && <TableHead>Mobile</TableHead>}
                  {columns.isVisible('city') && <TableHead>City</TableHead>}
                  {columns.isVisible('pincode') && <TableHead>Pincode</TableHead>}
                  {columns.isVisible('kydCompliant') && <TableHead>KYD Compliant</TableHead>}
                  {columns.isVisible('arnValidTill') && <TableHead>ARN Valid Date</TableHead>}
                  {columns.isVisible('tcStatus') && <TableHead>T&C Status</TableHead>}
                  {columns.isVisible('leadStatus') && <TableHead>Lead Status</TableHead>}
                  {columns.isVisible('paymentStatus') && <TableHead>Payment Status</TableHead>}
                  <TableHead className="text-right">
                    <span className="sr-only">Actions</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {/* Row activation is a real <button> in the ID cell, not
                    `role="button"` on the <tr>: that role is
                    children-presentational, which would strip the row's own
                    Edit/Delete buttons and every cell-header association from
                    the accessibility tree. The row keeps its mouse click. */}
                {rows.map((lead) => (
                  <TableRow
                    key={lead.id}
                    className="cursor-pointer whitespace-nowrap"
                    onClick={() => setSelectedLead(lead)}
                  >
                    {columns.isVisible('leadCode') && (
                      <TableCell className="font-medium">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedLead(lead);
                          }}
                          className="rounded underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          <span className="sr-only">{`Open ${leadFullName(lead)}: `}</span>
                          {lead.leadCode}
                        </button>
                      </TableCell>
                    )}
                    {columns.isVisible('arn') && <TableCell>{lead.arn}</TableCell>}
                    {/* `max-width` on a <td> is ignored under table-layout:auto,
                        so the clamp + truncate live on an inner block element. */}
                    {columns.isVisible('name') && (
                      <TableCell>
                        <div className="max-w-[14rem] truncate" title={leadFullName(lead)}>
                          {leadFullName(lead)}
                        </div>
                      </TableCell>
                    )}
                    {columns.isVisible('brandName') && (
                      <TableCell>
                        <div className="max-w-[12rem] truncate" title={lead.brandName ?? ''}>
                          {lead.brandName || '-'}
                        </div>
                      </TableCell>
                    )}
                    {columns.isVisible('email') && (
                      <TableCell className="text-muted-foreground">
                        <div className="max-w-[16rem] truncate" title={lead.email}>
                          {lead.email}
                        </div>
                      </TableCell>
                    )}
                    {columns.isVisible('mobile') && (
                      <TableCell>
                        {lead.mobile ? (
                          <a
                            href={`tel:${lead.mobile.replace(/[\s-]/g, '')}`}
                            onClick={(e) => e.stopPropagation()}
                            aria-label={`Call ${leadFullName(lead)} on ${lead.mobile}`}
                            className="inline-flex min-h-11 items-center rounded underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:min-h-0"
                          >
                            {lead.mobile}
                          </a>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                    )}
                    {columns.isVisible('city') && <TableCell>{lead.city ?? '-'}</TableCell>}
                    {columns.isVisible('pincode') && <TableCell>{lead.pincode ?? '-'}</TableCell>}
                    {/* Both AMFI columns. Read-only everywhere - there is no
                        edit affordance because there is no API that accepts
                        them; only a Fetch from ARN can change what they say. */}
                    {columns.isVisible('kydCompliant') && (
                      <TableCell>
                        <StatusChip tone={kydTone(lead.kydCompliant ?? null)}>
                          {kydLabel(lead.kydCompliant ?? null)}
                        </StatusChip>
                      </TableCell>
                    )}
                    {columns.isVisible('arnValidTill') && (
                      <TableCell>{formatValidTill(lead.arnValidTill ?? null)}</TableCell>
                    )}
                    {columns.isVisible('tcStatus') && (
                      <TableCell>
                        <StatusChip tone={tcTone(lead.tcStatus)}>{lead.tcStatus}</StatusChip>
                      </TableCell>
                    )}
                    {columns.isVisible('leadStatus') && (
                      <TableCell>
                        <StatusChip tone={leadTone(lead.leadStatus)}>{lead.leadStatus}</StatusChip>
                      </TableCell>
                    )}
                    {columns.isVisible('paymentStatus') && (
                      <TableCell>
                        <StatusChip tone={payTone(lead.paymentStatus)}>
                          {lead.paymentStatus}
                        </StatusChip>
                      </TableCell>
                    )}
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`Edit ${leadFullName(lead)}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditTarget(lead);
                        }}
                        className="mr-1 size-9 border border-brand sm:size-8"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`Delete ${leadFullName(lead)}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteTarget(lead);
                        }}
                        className="size-9 border border-destructive sm:size-8"
                      >
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>Add Lead</DialogTitle>
            <DialogDescription>
              Create a new prospective lead. ARN, first name, last name, mobile and email are
              required.
            </DialogDescription>
          </DialogHeader>
          {/*
            12 fields in a 3-column grid = 4 even rows, which is why the count is
            worth keeping in mind when adding one: a 13th field leaves a ragged
            last row. The client-type toggle above and the footer below each span
            the full width and sit outside those four rows.

            Two columns until `md`, one on a phone. Three columns only once the
            panel is wide enough for them - at `max-w-lg` each would be ~146px,
            too narrow for an email placeholder or the State/City comboboxes,
            which is why the panel widens to `max-w-3xl` alongside this.
          */}
          <form
            onSubmit={(e) => {
              void onAdd(e);
            }}
            className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3"
          >
            <div className="sm:col-span-2 md:col-span-3">
              <ClientTypeToggle
                value={addIsNonArn}
                onChange={(v) => setValue('isNonArn', v, { shouldValidate: true })}
              />
            </div>
            {!addIsNonArn && (
              <Field label="ARN" error={errors.arn?.message} warning={arnWarning}>
                <Input
                  {...register('arn')}
                  placeholder="1–6 digits"
                  maxLength={6}
                  inputMode="numeric"
                />
              </Field>
            )}
            {addIsNonArn && (
              <Field label="Client Code">
                <Input value="Assigned automatically (NA…)" disabled readOnly />
              </Field>
            )}
            <Field label="First Name" error={errors.firstName?.message}>
              <Input {...register('firstName')} placeholder="First name" />
            </Field>
            <Field label="Middle Name" optional error={errors.middleName?.message}>
              <Input {...register('middleName')} placeholder="Middle name" />
            </Field>
            <Field label="Last Name" error={errors.lastName?.message}>
              <Input {...register('lastName')} placeholder="Last name" />
            </Field>
            <Field label="Brand Name" optional error={errors.brandName?.message}>
              <Input
                {...register('brandName')}
                maxLength={150}
                placeholder="What people call the firm"
              />
            </Field>
            <Field label="Mobile" error={errors.mobile?.message}>
              <Input
                {...register('mobile')}
                type="tel"
                inputMode="numeric"
                maxLength={10}
                placeholder="10-digit mobile"
              />
            </Field>
            <Field label="Email" error={errors.email?.message}>
              <Input {...register('email')} placeholder="email@example.com" />
            </Field>
            <RhfStateCitySelect
              control={control}
              setValue={setValue}
              stateName="state"
              cityName="city"
              portalled={false}
              labelClassName="text-sm font-medium leading-none"
              stateClassName="gap-1"
              cityClassName="gap-1"
            />
            <Field label="Pincode" error={errors.pincode?.message}>
              <Input {...register('pincode')} placeholder="Pincode" />
            </Field>
            {/*
              Lead source (plan.md §16.2, task 9.7). OPTIONAL, and the placeholder
              says so rather than pre-selecting a channel: a required field an RM
              cannot answer gets answered at random, and invented attribution is
              worse than none because it cannot be told apart from the real thing.
              The blank stays blank and reads as "not recorded" on the MIS board.
            */}
            <Field label="Lead Source" optional error={errors.leadSource?.message}>
              <Controller
                name="leadSource"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <SelectMenu
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(e.target.value)}
                  >
                    <option value="">Not recorded</option>
                    {LEAD_SOURCE_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </SelectMenu>
                )}
              />
            </Field>
            <Field label="Source Detail" optional error={errors.leadSourceDetail?.message}>
              <Input
                {...register('leadSourceDetail')}
                maxLength={120}
                placeholder="Campaign, referrer or event"
              />
            </Field>
            <DialogFooter className="sm:col-span-2 md:col-span-3">
              <Button type="button" variant="outline" onClick={() => setAddOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                className="gap-1.5"
                disabled={isSubmitting || createLead.isPending}
              >
                <Check className="h-4 w-4" />
                Save Lead
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!editTarget}
        onOpenChange={(open) => {
          if (!open) setEditTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Lead</DialogTitle>
            <DialogDescription>Update the details entered for this lead.</DialogDescription>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              void onEdit(e);
            }}
            className="grid grid-cols-1 gap-3 sm:grid-cols-2"
          >
            <div className="sm:col-span-2">
              <ClientTypeToggle
                value={editIsNonArn}
                onChange={(v) => setValueEdit('isNonArn', v, { shouldValidate: true })}
              />
            </div>
            {!editIsNonArn && (
              <Field label="ARN" error={editErrors.arn?.message}>
                <Input
                  {...registerEdit('arn')}
                  placeholder="1–6 digits"
                  maxLength={6}
                  inputMode="numeric"
                />
              </Field>
            )}
            {editIsNonArn && (
              <Field label="Client Code">
                <Input
                  value={editTarget?.isNonArn ? editTarget.arn : 'Assigned automatically (NA…)'}
                  disabled
                  readOnly
                />
              </Field>
            )}
            <Field label="First Name" error={editErrors.firstName?.message}>
              <Input {...registerEdit('firstName')} placeholder="First name" />
            </Field>
            <Field label="Middle Name" optional error={editErrors.middleName?.message}>
              <Input {...registerEdit('middleName')} placeholder="Middle name" />
            </Field>
            <Field label="Last Name" error={editErrors.lastName?.message}>
              <Input {...registerEdit('lastName')} placeholder="Last name" />
            </Field>
            <Field label="Brand Name" optional error={editErrors.brandName?.message}>
              <Input
                {...registerEdit('brandName')}
                maxLength={150}
                placeholder="What people call the firm"
              />
            </Field>
            <Field label="Mobile" error={editErrors.mobile?.message}>
              <Input {...registerEdit('mobile')} placeholder="10-digit mobile" />
            </Field>
            <Field label="Email" error={editErrors.email?.message}>
              <Input {...registerEdit('email')} placeholder="email@example.com" />
            </Field>
            <RhfStateCitySelect
              control={controlEdit}
              setValue={setValueEdit}
              stateName="state"
              cityName="city"
              portalled={false}
              labelClassName="text-sm font-medium leading-none"
              stateClassName="gap-1"
              cityClassName="gap-1"
            />
            <Field label="Pincode" error={editErrors.pincode?.message}>
              <Input {...registerEdit('pincode')} placeholder="Pincode" />
            </Field>
            {/*
              AMFI facts, shown but never editable (Fix 1). Rendered as disabled
              inputs rather than omitted: an RM who cannot see the KYD status here
              has to open the wizard to find out whether this client is even
              allowed to transact. There is no form field behind either - they are
              not part of AddLeadFormValues at all - so nothing about them can be
              submitted, and the only thing that changes them is Fetch from ARN in
              Step 1 of the wizard.
            */}
            <Field label="KYD Compliant" helper="From AMFI. Updated by Fetch from ARN.">
              <Input value={kydLabel(editTarget?.kydCompliant ?? null)} disabled readOnly />
            </Field>
            <Field label="ARN Valid Date" helper="From AMFI. Updated by Fetch from ARN.">
              <Input value={formatValidTill(editTarget?.arnValidTill ?? null)} disabled readOnly />
            </Field>
            {/*
              Back-filling the channel on a lead that predates capture. The server
              records the correction but deliberately leaves the capture instant
              NULL, which is what keeps a reconstruction distinguishable from
              something observed at the moment the lead was created.

              A stored value the list no longer offers (an older label, or one of
              the importer/seed vocabularies) is rendered as its own option, so
              the dialog shows the truth and the form round-trips it byte-for-byte
              when the RM is here for some other field. The empty option is
              labelled for what it DOES: on a lead that has a channel, choosing it
              is a deletion, and it should read like one.
            */}
            <Field
              label="Lead Source"
              optional
              error={editErrors.leadSource?.message}
              warning={
                editSourceIsUnlisted
                  ? `"${editSourceStored}" is not in the current list. It is kept as recorded unless you change it here.`
                  : undefined
              }
            >
              <Controller
                name="leadSource"
                control={controlEdit}
                render={({ field }) => (
                  <SelectMenu
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(e.target.value)}
                  >
                    <option value="">
                      {editSourceStored === '' ? 'Not recorded' : 'Clear the recorded source'}
                    </option>
                    {editSourceIsUnlisted && (
                      <option
                        value={editSourceStored}
                      >{`${editSourceStored} (as recorded)`}</option>
                    )}
                    {LEAD_SOURCE_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </SelectMenu>
                )}
              />
            </Field>
            <Field label="Source Detail" optional error={editErrors.leadSourceDetail?.message}>
              <Input
                {...registerEdit('leadSourceDetail')}
                maxLength={120}
                placeholder="Campaign, referrer or event"
              />
            </Field>
            <DialogFooter className="sm:col-span-2">
              <Button type="button" variant="outline" onClick={() => setEditTarget(null)}>
                Cancel
              </Button>
              <Button
                type="submit"
                className="gap-1.5"
                disabled={isEditSubmitting || updateLead.isPending}
              >
                <Check className="h-4 w-4" />
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete this lead?</DialogTitle>
            <DialogDescription>
              This permanently deletes{' '}
              <span className="font-medium text-foreground">
                {deleteTarget ? leadFullName(deleteTarget) : ''}
              </span>{' '}
              (ARN {deleteTarget?.arn}) and cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={deleteLead.isPending}
              onClick={() => {
                if (!deleteTarget) return;
                deleteLead.mutate(deleteTarget.id, {
                  onSuccess: () => {
                    toast.success('Lead deleted');
                    setDeleteTarget(null);
                  },
                  onError: () => toast.error('Could not delete lead'),
                });
              }}
            >
              {deleteLead.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

interface FieldProps {
  label: string;
  error?: string;
  warning?: string;
  // Renders a muted "(optional)" beside the LABEL rather than inside the input's
  // placeholder (Fix 5). A placeholder disappears the moment anything is typed
  // and is invisible to a screen reader reading the field's name, so it is the
  // one place the word cannot do its job.
  optional?: boolean;
  // Static, always-visible note under the control. Distinct from `warning`,
  // which is conditional and flagged; this is for explaining what a field IS -
  // e.g. that a value comes from AMFI and cannot be typed.
  helper?: string;
  children: React.ReactNode;
}

interface ClientTypeToggleProps {
  value: boolean;
  onChange: (isNonArn: boolean) => void;
}

// Segmented ARN Holder / Non-ARN control for the add/edit lead dialogs
// (ADKJ-117). Non-ARN clients get a server-minted 'NA…' code instead of an
// AMFI registration number.
function ClientTypeToggle({ value, onChange }: ClientTypeToggleProps) {
  const optionClass = (active: boolean): string =>
    active
      ? 'rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-white shadow-sm'
      : 'rounded-md px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground';
  return (
    <div className="flex flex-col gap-1">
      <Label>Client Type</Label>
      <div className="inline-flex w-fit items-center gap-1 rounded-lg border border-border bg-muted/40 p-1">
        <button type="button" className={optionClass(!value)} onClick={() => onChange(false)}>
          ARN Holder
        </button>
        <button type="button" className={optionClass(value)} onClick={() => onChange(true)}>
          Non-ARN
        </button>
      </div>
    </div>
  );
}

function Field({ label, error, warning, optional, helper, children }: FieldProps) {
  const fieldId = useId();
  const msgId = `${fieldId}-msg`;
  const helpId = `${fieldId}-help`;
  const describedBy =
    [error || warning ? msgId : null, helper ? helpId : null].filter(Boolean).join(' ') ||
    undefined;
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <Label htmlFor={fieldId}>
        {label}
        {optional && <span className="ml-1 font-normal text-muted-foreground">(optional)</span>}
      </Label>
      {isValidElement(children)
        ? cloneElement(children as ReactElement<{ id?: string; 'aria-describedby'?: string }>, {
            id: fieldId,
            'aria-describedby': describedBy,
          })
        : children}
      {helper && (
        <span id={helpId} className="text-xs text-muted-foreground">
          {helper}
        </span>
      )}
      {error && (
        <span id={msgId} role="alert" className="text-xs text-destructive">
          {error}
        </span>
      )}
      {!error && warning && (
        <span id={msgId} role="status" className="flex items-center gap-1 text-xs text-warning">
          <AlertTriangle className="h-3 w-3" />
          {warning}
        </span>
      )}
    </div>
  );
}

export default SalesLeadsPage;
